import type { IModuleDefinition, IRecord, IRecordWrite, IUserRef } from '@platform/core/types';
import type { ILibraryCollection } from './ILibraryCollection';

/**
 * Бібліотеки бази знань (прототип v4 kbLibs) як записи прихованого типу модуля `<тип статей>_library` (kb → kb_library; метадані ADR
 * meta 08:45): назва — `title`, опис — `description`, власник — `owner` (користувач); статті й справи — масиви id у розширенні запису
 * (`ext.articles`, `ext.cases`, порядок прототипу), не зв'язки: добірка не змінює лічильників зв'язків статей і справ, як і в прототипі.
 * Чисті функції.
 */
export const COLLECTION_FIELDS = { name: 'title', description: 'description', owner: 'owner', articles: 'articles', cases: 'cases' } as const;

/** Ключі складу бібліотеки — зберігаються в розширенні запису (ext), а не в полях. */
const MEMBERS: readonly string[] = [COLLECTION_FIELDS.articles, COLLECTION_FIELDS.cases];

/** Тип записів бібліотек модуля для типу статей `rtKey`, якщо модуль його визначає; інакше undefined (бібліотек немає). */
export function collectionTypeOf(module: IModuleDefinition, rtKey: string): string | undefined {
  const key = `${rtKey}_library`;
  return module.recordTypes.some((t) => t.key === key) ? key : undefined;
}

/** id записів зі значення json-поля: масив рядків (рядок JSON з масивом теж приймається); інше — порожньо. */
export function idsOf(value: unknown): string[] {
  let v = value;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch { return []; }
  }
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x !== '') : [];
}

/** Бібліотека із запису добірки (склад — з ext; старі записи з полями articles/cases теж читаються). */
export function collectionOf(record: IRecord): ILibraryCollection {
  const v = record.values;
  const owner = v[COLLECTION_FIELDS.owner] as IUserRef | null | undefined;
  const description = v[COLLECTION_FIELDS.description];
  return {
    record, name: record.title || String(v[COLLECTION_FIELDS.name] ?? ''), description: typeof description === 'string' ? description : '',
    owner: owner && typeof owner === 'object' ? owner.name : '',
    articleIds: idsOf(record.ext?.[COLLECTION_FIELDS.articles] ?? v[COLLECTION_FIELDS.articles]), caseIds: idsOf(record.ext?.[COLLECTION_FIELDS.cases] ?? v[COLLECTION_FIELDS.cases]),
  };
}

/** Записи за списком id у його порядку (невідомі id пропускаються — статтю чи справу могли видалити). */
export function recordsByIds(ids: readonly string[], records: readonly IRecord[]): IRecord[] {
  const byId = new Map(records.map((r) => [r.id, r]));
  return ids.map((id) => byId.get(id)).filter((r): r is IRecord => r !== undefined);
}

/** Записи за номером (порядок записів прототипу — для пунктів «+ Додати статтю» і «+ Зв’язати справу»). Вхід не змінює. */
export function byCode(records: readonly IRecord[]): IRecord[] {
  return [...records].sort((a, b) => a.code - b.code);
}

/** Тіло зміни бібліотеки: назва й опис — у полях, склад (articles, cases) — у розширенні поверх наявного (інші ключі ext зберігаються). */
export function collectionWrite(collection: ILibraryCollection, changes: Readonly<Record<string, unknown>>): IRecordWrite {
  const values: Record<string, unknown> = {};
  const ext: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(changes)) (MEMBERS.includes(k) ? ext : values)[k] = v;
  return Object.keys(ext).length ? { values, ext: { ...collection.record.ext, ...ext } } : { values };
}
